/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import { ViewByType } from 'types/traces';

import { TreeDataItem } from '@/components/treeview-dropdown';

export function convertToTree(data: Record<string, string>): TreeDataItem[] {
  const root: Record<string, any> = {};

  // Build a nested object structure from dot-separated keys
  for (const key in data) {
    const parts = key.split('.');
    let current = root;

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];

      if (!current[part]) {
        current[part] = {
          __key: parts.slice(0, i + 1).join('.'),
          __name: i === parts.length - 1 ? data[key] : '',
          __children: {},
        };
      }

      current = current[part].__children;
    }
  }

  // Convert the nested structure into TreeDataItem[]
  function buildTree(node: any): TreeDataItem[] {
    return Object.entries(node).map(([key, value]: [string, any]) => {
      const item: TreeDataItem = {
        id: value.__key,
        name: value.__name || key,
      };

      const children = buildTree(value.__children);
      if (children.length > 0) item.children = children;

      return item;
    });
  }

  return buildTree(root);
}

//a function to search for a tree node by key
export function findNodeByKey(
  treeData: TreeDataItem[],
  key: string
): TreeDataItem | null {
  for (const node of treeData) {
    if (node.id === key) {
      return node;
    }
    if (node.children) {
      const foundNode = findNodeByKey(node.children, key);
      if (foundNode) {
        return foundNode;
      }
    }
  }
  return null;
}

// function the enable/disable the dropdown items based on view mode: performance or reliability.
// in performance mode, all items are enabled, except error nodes.
// in reliability mode, error nodes are enabled, and all other nodes are disabled.
export function enableDisableTreeItems(
  treeData: TreeDataItem[],
  isPerformanceMode: ViewByType
): TreeDataItem[] {
  // we're going to disable all items that have 'error' in their id

  return treeData.map((item) => {
    const newItem = { ...item };
    if (isPerformanceMode === 'all') {
      newItem.disabled = false;
    } else {
      if (newItem.id && newItem.id.includes('error')) {
        newItem.disabled = isPerformanceMode === 'performance';
      } else {
        newItem.disabled = isPerformanceMode === 'reliability';
      }
    }
    if (newItem.children) {
      newItem.children = enableDisableTreeItems(
        newItem.children,
        isPerformanceMode
      );
    }
    return newItem;
  });
}
